import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Avatar } from "../components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Check, X, UserPlus, Search, Loader2, ArrowDownWideNarrow } from "lucide-react";
import { toast } from "sonner";

export default function Friends() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("alpha");

  const load = async () => {
    const [r, f] = await Promise.all([api.get("/friends/requests"), api.get("/friends")]);
    setRequests(r.data); setFriends(f.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get(`/users/search?q=${encodeURIComponent(q)}`);
      setResults(data);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const respond = async (request_id, action) => {
    try { await api.post("/friends/respond", { request_id, action }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const addFriend = async (uid) => {
    try { const { data } = await api.post("/friends/request", { to_user_id: uid });
      toast.success(data.guardian_status === "pending" ? "Awaiting guardian approval" : "Request sent"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-spotter-maroon" /></div>;

  const sortedFriends = [...friends].sort((a, b) => {
    if (sort === "alpha") return (a.name || "").localeCompare(b.name || "");
    if (sort === "recent") return (b.friend_since || "").localeCompare(a.friend_since || "");
    if (sort === "oldest") return (a.friend_since || "").localeCompare(b.friend_since || "");
    if (sort === "spots") return (b.spots || 0) - (a.spots || 0);
    return 0;
  });

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <h2 className="font-display text-3xl font-bold text-spotter-maroon mb-3">Find Members</h2>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-spotter-maroon" />
          <Input data-testid="friends-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or username…" className="pl-9 border-2 border-black" />
        </div>
        {results.length > 0 && (
          <div className="mt-3 space-y-2">
            {results.map((u) => (
              <div key={u.id} className="flex items-center gap-3 bg-white border-2 border-black rounded-xl p-3" data-testid={`find-${u.id}`}>
                <button onClick={() => navigate(`/profile/${u.id}`)}><Avatar user={u} size="md" showPresence /></button>
                <div className="flex-1"><div className="font-bold">{u.name}</div><div className="text-xs text-spotter-maroon">@{u.username}</div></div>
                <Button data-testid={`add-${u.id}`} onClick={() => addFriend(u.id)} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><UserPlus className="w-4 h-4" />Add</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-3">Pride Requests {requests.length > 0 && `(${requests.length})`}</h3>
        {requests.length === 0 ? <p className="text-spotter-maroon">No pending requests.</p> : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.request_id} className="flex items-center gap-3 bg-white border-2 border-black rounded-xl p-3" data-testid={`request-${r.request_id}`}>
                <button onClick={() => navigate(`/profile/${r.user.id}`)}><Avatar user={r.user} size="md" showPresence /></button>
                <div className="flex-1"><div className="font-bold">{r.user.name}</div><div className="text-xs text-spotter-maroon">@{r.user.username}</div></div>
                <Button data-testid={`accept-${r.request_id}`} onClick={() => respond(r.request_id, "accept")} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Check className="w-4 h-4" /></Button>
                <Button data-testid={`decline-${r.request_id}`} onClick={() => respond(r.request_id, "decline")} variant="outline" className="border-2 border-black gap-1"><X className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <h3 className="font-display text-2xl font-bold text-spotter-maroon">Your Pride {friends.length > 0 && `(${friends.length})`}</h3>
          {friends.length > 1 && (
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="friends-sort" className="w-auto gap-1 border-2 border-black bg-white h-9 text-sm">
                <ArrowDownWideNarrow className="w-4 h-4" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alpha" data-testid="friends-sort-alpha">Alphabetical (A–Z)</SelectItem>
                <SelectItem value="recent" data-testid="friends-sort-recent">Recently added</SelectItem>
                <SelectItem value="oldest" data-testid="friends-sort-oldest">Oldest first</SelectItem>
                <SelectItem value="spots" data-testid="friends-sort-spots">Most spots</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        {friends.length === 0 ? <p className="text-spotter-maroon">No Pride yet — find some members above!</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {sortedFriends.map((u) => (
              <button key={u.id} data-testid={`friend-${u.id}`} onClick={() => navigate(`/profile/${u.id}`)}
                className="flex flex-col items-center gap-2 bg-white border-2 border-black rounded-xl p-4 hover:bg-spotter-cream">
                <Avatar user={u} size="lg" showPresence />
                <div className="font-bold text-sm text-center">{u.name}</div>
                <div className="text-xs text-spotter-maroon">@{u.username}</div>
                {sort === "spots" && <div className="text-[11px] font-bold text-spotter-maroon">{u.spots || 0} spots</div>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
